import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PLANBASE_CONFIG } from '../config';
import { decodeJWT } from '../utils/auth';
import posthog from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

interface UserMetadata {
  name?: string;
  title?: string;
  avatar_url?: string;
  oauth_provider?: string;
  oauth_provider_id?: string;
  [key: string]: any;
}

interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  title?: string;
  metadata?: UserMetadata;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  signup: (name: string, email: string, password: string, title?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();

    const handleAuthChange = () => { checkAuthStatus(); };
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GATELY_OAUTH_SUCCESS') { checkAuthStatus(); }
    };

    window.addEventListener('auth-changed', handleAuthChange);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('auth-changed', handleAuthChange);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const checkAuthStatus = async () => {
    // DEV-ONLY: local bypass. Tree-shaken in production because import.meta.env.DEV is false.
    // Turn on: visit http://localhost:5174/?bypass=1  (or run localStorage.setItem('planbase_dev_bypass','1'))
    // Turn off: visit http://localhost:5174/?bypass=0  (or run localStorage.removeItem('planbase_dev_bypass'))
    if (import.meta.env.DEV) {
      const url = new URL(window.location.href);
      const bypassParam = url.searchParams.get('bypass');
      if (bypassParam === '1') {
        localStorage.setItem('planbase_dev_bypass', '1');
        url.searchParams.delete('bypass');
        window.history.replaceState({}, '', url.toString());
      } else if (bypassParam === '0') {
        localStorage.removeItem('planbase_dev_bypass');
        localStorage.removeItem('planbase_token');
        localStorage.removeItem('planbase_user');
        url.searchParams.delete('bypass');
        window.history.replaceState({}, '', url.toString());
      }
      if (localStorage.getItem('planbase_dev_bypass') === '1') {
        const fakeUser: User = {
          id: 'dev-bypass-user',
          email: 'dev@planbase.local',
          name: 'Dev User',
          title: 'Architect',
          metadata: {
            plan: 'pro',
            tier: 'pro',
            plan_id: 'dev-bypass-plan',
            subscription_status: 'active',
          },
        };
        localStorage.setItem('planbase_user', JSON.stringify(fakeUser));
        localStorage.setItem('planbase_token', 'dev-bypass-token');
        setUser(fakeUser);
        setLoading(false);
        return;
      }
    }

    const token = localStorage.getItem('planbase_token');
    const userData = localStorage.getItem('planbase_user');

    if (token && userData) {
      try {
        const payload = decodeJWT(token);
        const isExpired = payload?.exp && payload.exp * 1000 < Date.now();
        
        if (isExpired) {
          localStorage.removeItem('planbase_token');
          localStorage.removeItem('planbase_user');
          setUser(null);
          setLoading(false);
          return;
        }
        
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        fetchAndUpdatePlan(parsedUser, token);
      } catch (error) {
        localStorage.removeItem('planbase_token');
        localStorage.removeItem('planbase_user');
      }
    }
    setLoading(false);
  };

  const fetchAndUpdatePlan = async (currentUser: any, token: string) => {
    try {
      const res = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Project-ID': PLANBASE_CONFIG.projectId,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) return;

      const data = await res.json();
      const member = data.user || data.member || data;
      const planId = member.plan_id;
      const subscriptionStatus = member.subscription_status;

      if (!planId) return;

      const planType = 'pro';
      if (currentUser?.metadata?.plan !== planType || currentUser?.metadata?.plan_id !== planId) {
        const updated = {
          ...currentUser,
          metadata: {
            ...currentUser.metadata,
            plan: planType,
            plan_id: planId,
            subscription_status: subscriptionStatus,
          },
        };
        localStorage.setItem('planbase_user', JSON.stringify(updated));
        setUser(updated);
      }
    } catch { /* silent */ }
  };

  const extractUserData = (apiUser: any): User => {
    let metadata: any = {};
    
    if (typeof apiUser.metadata === 'string') {
      try { metadata = JSON.parse(apiUser.metadata); } catch {}
    } else if (apiUser.metadata) {
      metadata = apiUser.metadata;
    } else if (apiUser.user_metadata) {
      metadata = apiUser.user_metadata.metadata || apiUser.user_metadata;
    }

    // Map plan_id on project_members to a plan tier name
    // plan_id is the UUID of the plan — we store it and use it to determine access
    const planId = apiUser.plan_id || metadata.plan_id;
    const subscriptionStatus = apiUser.subscription_status || metadata.subscription_status;
    // If they have an active plan_id and subscription is active/trialing, mark as pro
    const plan = metadata.plan ||
      (planId && ['active', 'trialing', null, undefined].includes(subscriptionStatus) ? 'pro' : 'free');
    
    return {
      id: apiUser.id,
      email: apiUser.email,
      name: metadata.full_name || metadata.name || apiUser.email?.split('@')[0] || 'User',
      avatar_url: metadata.avatar_url,
      title: metadata.title || 'Architect',
      metadata: {
        ...metadata,
        plan,
        plan_id: planId,
        subscription_status: subscriptionStatus,
      }
    };
  };

  const login = async (email: string, password: string) => {
    const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        project_id: PLANBASE_CONFIG.projectId
      })
    });

    const data = await response.json();

    if (response.ok && data.session) {
      const userData = extractUserData(data.user);
      
      localStorage.setItem('planbase_token', data.session.access_token);
      localStorage.setItem('planbase_user', JSON.stringify(userData));
      setUser(userData);
      posthog.identify(userData.id, { email: userData.email, name: userData.name });
    } else {
      throw new Error(data.error || 'Incorrect email or password');
    }
  };

  const loginWithGoogle = useCallback((): Promise<void> => {
    // On native iOS/Android, use the Capacitor Google Auth plugin (no popup/browser)
    if (Capacitor.isNativePlatform()) {
      return loginWithGoogleNative();
    }
    // On web, fall back to the existing popup flow
    return loginWithGoogleWeb();
  }, []);

  const loginWithGoogleNative = async (): Promise<void> => {
    // Initialize on first use (safe to call multiple times)
    GoogleAuth.initialize({
      clientId: PLANBASE_CONFIG.googleClientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });

    const googleUser = await GoogleAuth.signIn();

    const idToken = googleUser.authentication?.idToken;
    if (!idToken) {
      throw new Error('Google sign-in did not return an ID token');
    }

    // Exchange the Google ID token with your backend
    const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/oauth/google/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_token: idToken,
        project_id: PLANBASE_CONFIG.projectId,
      }),
    });

    const data = await response.json();

    if (response.ok && data.session) {
      const userData = extractUserData(data.user);
      localStorage.setItem('planbase_token', data.session.access_token);
      localStorage.setItem('planbase_user', JSON.stringify(userData));
      setUser(userData);
      posthog.identify(userData.id, { email: userData.email, name: userData.name });
    } else {
      throw new Error(data.error || 'Google sign-in failed');
    }
  };

  const loginWithGoogleWeb = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const callbackUrl = `${window.location.origin}/auth/callback`;
      const googleAuthUrl = `${PLANBASE_CONFIG.oauthApiUrl}/sdk/sso/google?project_id=${PLANBASE_CONFIG.projectId}&redirect_to=${encodeURIComponent(callbackUrl)}`;

      // Open a centered popup
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        googleAuthUrl,
        'gately_google_oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        reject(new Error('Popup was blocked. Please allow popups for this site and try again.'));
        return;
      }

      let resolved = false;

      const finish = (success: boolean, err?: Error) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        if (success) resolve();
        else reject(err);
      };

      // Listen for postMessage from the callback page
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'GATELY_OAUTH_SUCCESS') {
          checkAuthStatus();
          finish(true);
        } else if (event.data?.type === 'GATELY_OAUTH_ERROR') {
          finish(false, new Error(event.data.error || 'Google login failed'));
        }
      };

      // Poll localStorage as fallback
      const pollTimer = setInterval(() => {
        const token = localStorage.getItem('planbase_token');
        if (token) {
          checkAuthStatus();
          finish(true);
          return;
        }
        if (popup.closed) {
          finish(false, new Error('Google login was cancelled'));
        }
      }, 500);

      const cleanup = () => {
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);
        if (!popup.closed) popup.close();
      };

      window.addEventListener('message', handleMessage);
    });
  };

  const signup = async (name: string, email: string, password: string, title?: string) => {
    const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        project_id: PLANBASE_CONFIG.projectId,
        metadata: {
          name,
          title: title || 'Architect'
        }
      })
    });

    const data = await response.json();

    if (response.ok && data.session) {
      const userData = extractUserData(data.user);
      
      localStorage.setItem('planbase_token', data.session.access_token);
      localStorage.setItem('planbase_user', JSON.stringify(userData));
      setUser(userData);
      posthog.identify(userData.id, { email: userData.email, name: userData.name });
    } else {
      throw new Error(data.error || 'Signup failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('planbase_token');
    localStorage.removeItem('planbase_user');
    setUser(null);
    posthog.reset();
  };

  const refreshUser = () => {
    checkAuthStatus();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
